import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Spinner, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getActivityLog } from "../../../helpers/firestoreService";
import { formatTimeAgo } from "../../../helpers/notificationDisplay";
import { useAuthUser } from "../../../hooks/AuthProvider";
import Paginations from "../../../components/Common/Pagination";
import homeBg from "../../../assets/images/home.png";

const PER_PAGE = 15;

const TYPE_ICONS = {
  property_added: "bx bx-building-house",
  lease_created: "bx bx-file",
  tenant_invited: "bx bx-user-plus",
};

/**
 * A true chronological audit trail of everything that happened in this PM's
 * portfolio (property added, payment setup created, tenant invited, etc.) —
 * NOT a search tool. Tickets are intentionally self-contained (their own
 * list/detail/chat under /tickets and /tenant/tickets) and are not mixed in
 * here.
 */
const AuditLog = () => {
  document.title = "Audit Log | Porcher";

  const user = useAuthUser();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const log = await getActivityLog(user.uid);
        setEntries(log);
      } catch (e) {
        console.error("Audit log load error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return entries.slice(start, start + PER_PAGE);
  }, [entries, currentPage]);

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Hero Banner ──────────────────────────────────────────────────── */}
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 140 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${homeBg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.18 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h3 className="mb-1 text-white">Audit Log</h3>
            <p className="mb-0 opacity-75" style={{ maxWidth: 580 }}>
              A chronological record of everything that's happened across your portfolio.
            </p>
          </div>
        </div>

        {entries.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-5">
              <i className="bx bx-history text-muted" style={{ fontSize: "2.5rem" }} />
              <p className="text-muted mt-2 mb-0">Nothing logged yet — actions like adding a property show up here.</p>
            </CardBody>
          </Card>
        ) : (
          <>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-0">
                {pageItems.map((entry) => (
                  <div
                    key={entry.id}
                    role={entry.link ? "button" : undefined}
                    tabIndex={entry.link ? 0 : undefined}
                    className="d-flex align-items-start gap-3 p-3 border-bottom"
                    style={entry.link ? { cursor: "pointer" } : undefined}
                    onClick={() => entry.link && navigate(entry.link)}
                    onKeyDown={(e) => { if (entry.link && e.key === "Enter") navigate(entry.link); }}
                  >
                    <div className="avatar-xs flex-shrink-0">
                      <span className="avatar-title bg-primary rounded-circle font-size-16">
                        <i className={TYPE_ICONS[entry.type] || "bx bx-info-circle"} />
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{entry.title || entry.type}</h6>
                      {entry.message && <p className="text-muted small mb-1">{entry.message}</p>}
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        <i className="mdi mdi-clock-outline me-1" />
                        {formatTimeAgo(entry.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>

            {entries.length > PER_PAGE && (
              <div className="mt-3">
                <Paginations
                  data={entries}
                  perPageData={PER_PAGE}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  isShowingPageLength
                  paginationDiv="col-auto"
                  paginationClass="pagination pagination-rounded mb-0"
                />
              </div>
            )}
          </>
        )}

      </Container>
    </div>
  );
};

export default AuditLog;

